import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home, Calendar, Users, Building2, UserPlus, BookOpen, Sparkles, FileText, Globe, MapPin, Mic2, Star } from 'lucide-react';
import ThemeSwitcher from './ThemeSwitcher';
import { useSiteConfig } from '../context/SiteConfigContext';

// Icon mapping for dynamic navigation
const iconMap = {
  home: Home,
  calendar: Calendar,
  users: Users,
  building: Building2,
  book: BookOpen,
  sparkles: Sparkles,
  file: FileText,
  globe: Globe,
  map: MapPin,
  mic: Mic2,
  star: Star,
  default: FileText
};

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('dim');
  const [hasSponsorBanner, setHasSponsorBanner] = useState(false);
  const location = useLocation();
  const { getNavigationItems, getLogo, navigation, branding, isRegistrationActive } = useSiteConfig();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Listen for theme changes to swap logo
  useEffect(() => {
    const checkTheme = () => {
      const theme = localStorage.getItem('theme') || 'dim';
      setCurrentTheme(theme);
    };
    checkTheme();
    const handleThemeChange = (e) => setCurrentTheme(e.detail);
    window.addEventListener('themechange', handleThemeChange);
    window.addEventListener('storage', checkTheme);
    return () => {
      window.removeEventListener('themechange', handleThemeChange);
      window.removeEventListener('storage', checkTheme);
    };
  }, []);

  // Detect if sponsor banner is present
  useEffect(() => {
    const check = () => {
      const banner = document.querySelector('[data-testid="sponsor-hero-banner"]');
      setHasSponsorBanner(!!banner);
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Get navigation items from site config
  const navItems = getNavigationItems();
  
  // Map items to include icons
  const navLinks = navItems.map(item => ({
    path: item.path,
    label: item.label,
    icon: iconMap[item.icon] || iconMap.default,
    external: item.external
  }));

  const isActive = (path) => location.pathname === path;

  // Select logo based on theme
  const logoSrc = currentTheme === 'light' 
    ? '/jts_nav_light_hq.png'
    : '/jts_nav_dark_hq.png';

  return (
    <nav
      className={`fixed left-1/2 -translate-x-1/2 z-50 transition-all duration-500 ${
        hasSponsorBanner ? 'top-10' : 'top-4'
      } ${
        scrolled ? 'w-[96%] max-w-7xl' : 'w-[94%] max-w-7xl'
      }`}
      data-testid="main-navigation"
    >
      <div className="rounded-full px-4 md:px-6 py-2 md:py-3 flex items-center justify-between bg-background/90 backdrop-blur-xl border border-border/50 shadow-lg overflow-hidden">
        {/* Official Brand Logo */}
        <Link 
          to="/" 
          className="flex items-center gap-3 group flex-shrink-0"
          data-testid="nav-logo"
        >
          <div className="flex items-center gap-2.5">
            <img 
              src="/jts_logo_stacked_dark.png"
              alt=""
              className={`object-contain rounded transition-all duration-300 ${scrolled ? 'h-8 w-8 md:h-9 md:w-9' : 'h-10 w-10 md:h-11 md:w-11'}`}
            />
            <span className={`font-playfair font-bold leading-tight tracking-tight text-foreground transition-all duration-300 ${scrolled ? 'text-sm md:text-base' : 'text-base md:text-lg'}`}>
              The Jock<br />Tax Summit
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        {(() => {
          const count = navLinks.length;
          const sizeClass = count <= 5 ? 'text-base px-4' : count <= 7 ? 'text-sm px-3' : 'text-sm px-2';
          return (
            <div className={`hidden lg:flex items-center gap-0.5 flex-shrink min-w-0 overflow-x-auto scrollbar-none`}>
              {navLinks.map((link) => {
                const cls = `${sizeClass.split(' ').filter(c => c.startsWith('px-') || c.startsWith('text-')).join(' ')} py-1.5 rounded-full font-medium transition-colors duration-300 whitespace-nowrap`;
                return link.external ? (
                  <a key={link.path} href={link.path} target="_blank" rel="noopener noreferrer"
                    data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`${cls} text-foreground/80 hover:text-foreground hover:bg-foreground/5`}>
                    {link.label}
                  </a>
                ) : (
                  <Link key={link.path} to={link.path}
                    data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`${cls} ${isActive(link.path) ? 'bg-primary text-primary-foreground' : 'text-foreground/80 hover:text-foreground hover:bg-foreground/5'}`}>
                    {link.label}
                  </Link>
                );
              })}
            </div>
          );
        })()}

        {/* Right side */}
        <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
          {navigation?.show_register_button !== false && isRegistrationActive && (
            <Link
              to={navigation?.register_button_path || "/signup"}
              className="hidden sm:flex items-center gap-1.5 px-3 xl:px-4 py-1.5 xl:py-2 bg-primary text-primary-foreground rounded-full text-xs xl:text-sm font-medium transition-transform hover:-translate-y-0.5"
              data-testid="nav-register-btn"
            >
              <UserPlus className="w-3.5 h-3.5 xl:w-4 xl:h-4" />
              <span className="hidden md:inline">{navigation?.register_button_text || "Register"}</span>
            </Link>
          )}
          <ThemeSwitcher />
          
          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-full hover:bg-foreground/5 transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div 
          className="lg:hidden mt-2 rounded-2xl p-4 animate-in slide-in-from-top-2 duration-300 bg-background/95 backdrop-blur-xl border border-border/50 shadow-lg"
          data-testid="mobile-menu"
        >
          {navLinks.map((link) => (
            link.external ? (
              <a
                key={link.path}
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-foreground hover:bg-foreground/5"
              >
                <link.icon className="w-5 h-5" />
                {link.label}
              </a>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive(link.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-foreground/5'
                }`}
              >
                <link.icon className="w-5 h-5" />
                {link.label}
              </Link>
            )
          ))}
          {navigation?.show_register_button !== false && isRegistrationActive && (
            <Link
              to={navigation?.register_button_path || "/signup"}
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl mt-2 bg-primary text-primary-foreground"
            >
              <UserPlus className="w-5 h-5" />
              {navigation?.register_button_text || "Register"}
            </Link>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navigation;
